import { useEffect, useState } from "react";

import { apiRequest, ensureCsrfToken } from "../../../api";

import Swal from "sweetalert2";

const TABS = [
  { key: "field_matrix", label: "Field Matrix" },
  { key: "radio_matrix", label: "Radio Matrix" },
];

export default function ApprovalMatrix() {
  const [activeTab, setActiveTab] = useState("field_matrix");
  const [matrices, setMatrices] = useState({ field_matrix: [], radio_matrix: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadMatrix() {
      setLoading(true);
      setError("");
      try {
        const response = await apiRequest("/approval-matrix/");
        if (!cancelled) {
          setMatrices(response.matrices || { field_matrix: [], radio_matrix: [] });
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load approval matrix.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadMatrix();
    return () => {
      cancelled = true;
    };
  }, []);

  function updateFieldRow(index, key, value) {
    setMatrices((current) => ({
      ...current,
      field_matrix: current.field_matrix.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  }

  function updateRadioRow(index, subIndex, value) {
    setMatrices((current) => ({
      ...current,
      radio_matrix: current.radio_matrix.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              sub: row.sub.map((subRow, currentSubIndex) =>
                currentSubIndex === subIndex
                  ? { ...subRow, checker_approval: value }
                  : subRow,
              ),
            }
          : row,
      ),
    }));
  }

  async function handleSave() {
    setSaving(true);

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/approval-matrix/save/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          matrix_type: activeTab,
          matrix: matrices[activeTab],
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.error || "Unable to save approval matrix.");
      }

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: payload.message || "Approval matrix saved successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: requestError.message || "Unable to save approval matrix.",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="workspace-panel">Loading approval matrix...</div>;
  }

  return (
    <section className="roles-view">
      

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="workspace-panel">
        <div className="template-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`template-tab${activeTab === tab.key ? " is-active" : ""}`}
              type="button"
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="config-table">
          {activeTab === "field_matrix" ? (
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Processor Edit</th>
                  <th>Checker Approval</th>
                  <th>Checker Edit</th>
                </tr>
              </thead>
              <tbody>
                {(matrices.field_matrix || []).map((row, index) => (
                  <tr key={row.parameter}>
                    <td>{row.parameter}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(row.processor_edit)}
                        onChange={(event) => updateFieldRow(index, "processor_edit", event.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(row.checker_approval)}
                        onChange={(event) => updateFieldRow(index, "checker_approval", event.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(row.checker_edit)}
                        onChange={(event) => updateFieldRow(index, "checker_edit", event.target.checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Checker Approval</th>
                </tr>
              </thead>
              <tbody>
                {(matrices.radio_matrix || []).map((row, index) => (
                  row.sub.map((subRow, subIndex) => (
                    <tr key={`${row.parameter}-${subRow.name}`}>
                      <td>
                        {subIndex === 0 ? <strong>{row.parameter}</strong> : null}
                        <div className="roles-radio-label--sub">{subRow.name}</div>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(subRow.checker_approval)}
                          onChange={(event) => updateRadioRow(index, subIndex, event.target.checked)}
                        />
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="template-actions">
          <button className="primary-button" type="button" disabled={saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save Matrix"}
          </button>
        </div>
      </div>
    </section>
  );
}
